import React, { useState, useEffect } from 'react';
import { Phone, PhoneCall, Users, Settings, Zap, CheckCircle } from 'lucide-react';

import Section from '../../components/navbar/internet/Phone/section';
import CTA from '../../components/navbar/internet/Phone/cta';
import Footer from '../../components/footer';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';

const IPPhoneWebsite = () => {
  const { isDarkMode } = useDarkMode();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: <Phone className="w-8 h-8" />,
      title: "Nomor Virtual (DID)",
      description: "Nomor perusahaan tanpa line fisik, bisa akses internasional",
      color: "from-green-700 to-green-900"
    },
    {
      icon: <PhoneCall className="w-8 h-8" />,
      title: "Call Forwarding & Transfer",
      description: "Alihkan panggilan ke nomor atau transfer antar extension",
      color: "from-green-600 to-green-800"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Conference Call",
      description: "Telekonferensi dengan banyak peserta sekaligus",
      color: "from-green-600 to-green-800"
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Auto Attendant / IVR",
      description: "Menu interaktif otomatis untuk pengaturan panggilan",
      color: "from-green-600 to-green-800"
    }
  ];

  const advantages = [
    "Panggilan Suara Berkualitas Tinggi",
    "Kualitas jernih dengan teknologi HD voice",
    "Voicemail to Email",
    "Pesan suara dikirim ke email dalam bentuk audio",
    "Integrasi dengan CRM / Call Center",
    "Terhubung dengan software bisnis untuk meningkatkan efisiensi",
    "Keamanan (Encryption)",
    "Panggilan aman dengan enkripsi untuk keamanan data"
  ];

  const techAdvantages = [
    "Skalabilitas - Tambah user baru dengan mudah",
    "Multi Device Support - Bisa digunakan lewat IP Phone, PC, atau smartphone",
    "Hemat Biaya - Biaya telepon lebih rendah daripada PSTN konvensional",
    "High Availability - Server redundan dengan uptime tinggi"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className={`min-h-screen bg-cover bg-center ${isDarkMode ? 'bg-gray-900' : 'bg-[#dfe4e0ff]'} text-slate-800`}
      style={{
        backgroundImage: `url('https://telnet.net.id/assets/bg.png')`, // Replace with your own image URL
        backgroundBlendMode: 'overlay',
      }}
    >
      {/* Hero Section */}

      {/* Hero Section */}
      <Section />

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-slate-800">
              Fitur Utama
              <span className="bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                {" "}IP Phone
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Solusi komunikasi lengkap untuk meningkatkan produktivitas bisnis Anda
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`p-6 rounded-2xl border cursor-pointer transition-all duration-500 ${
                    activeFeature === index 
                      ? 'bg-gradient-to-r from-green-50 to-white border-green-300 shadow-xl transform scale-105' 
                      : 'bg-white/70 border-green-200/50 hover:border-green-300/70'
                  }`}
                  onClick={() => setActiveFeature(index)}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${feature.color} bg-opacity-20`}>
                      <div className="text-slate-700">
                        {feature.icon}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2 text-slate-800">{feature.title}</h3>
                      <p className="text-slate-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-white/80 to-blue-50/60 rounded-3xl p-8 border border-blue-200/50 backdrop-blur-sm shadow-lg">
                <div className={`transition-all duration-500 transform ${activeFeature !== null ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-r ${features[activeFeature]?.color} bg-opacity-20 flex items-center justify-center mb-6 mx-auto`}>
                    <div className="text-slate-700">
                      {features[activeFeature]?.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-center mb-4 text-slate-800">
                    {features[activeFeature]?.title}
                  </h3>
                  <p className="text-slate-600 text-center text-lg">
                    {features[activeFeature]?.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
        <section id="advantages"  className={`min-h-screen bg-cover bg-center ${isDarkMode ? 'bg-gray-900' : 'bg-[#dfe4e0ff]'} text-slate-800`}
        style={{
          backgroundImage: `url('https://telnet.net.id/assets/bg.png')`, // Replace with your own image URL
          backgroundBlendMode: 'overlay',
        }}
        >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-slate-800">
              Keunggulan
              <span className="bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                {" "}Teknis
              </span>
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <h3 className="text-2xl font-bold text-green-600 mb-6">Fitur Premium</h3>
              {advantages.map((advantage, index) => (
                <div key={index} className="flex items-start space-x-4 group">
                  {index % 2 === 0 ? (
                    <div className="p-2 bg-gradient-to-r from-green-100 to-cyan-100 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  ) : (
                    <div className="flex-1 bg-white/70 rounded-lg p-4 border border-green-200/50 group-hover:border-green-300/50 transition-colors">
                      <p className="text-slate-700">{advantage}</p>
                    </div>
                  )}
                  
                  {index % 2 === 0 && (
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg mb-1 text-slate-800">{advantage}</h4>
                      <p className="text-slate-600">{advantages[index + 1]}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-8">
              <h3 className="text-2xl font-bold text-green-600 mb-6">Keunggulan Bisnis</h3>
              <div className="grid gap-6">
                {techAdvantages.map((tech, index) => (
                  <div 
                    key={index}
                    className="bg-gradient-to-r from-white/70 to-green-50/60 rounded-xl p-6 border border-green-200/50 hover:border-green-300/70 transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <Zap className="w-6 h-6 text-green-600" />
                      <p className="text-slate-700 font-medium">{tech}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default IPPhoneWebsite;